import React from "react";
import { View } from "react-native";
import { createStackNavigator } from "@react-navigation/stack";
import { Header } from "../../components/new/header/header";
import { translate } from "../../i18n";
import { useReservationInformation } from "../../hooks/useReservationInformation";
import { ReviewPayment } from "../../screens/home/review-payment";
import { PaymentSummary } from "../../screens/home/payment-summary";
import { AddCreditCard } from "../../screens/home/add-credit-card";
import { VerifyBillingAddress } from "../../screens/home/verify-billing-address";
import { AddBankAccount } from "../../screens/home/add-bank-account";
import { AppMode, PaymentActionTypes } from "../../enums";
import { DCashRecoveryEnterPaymentDetails } from "../../screens/more/dcash-recovery-enter-payment-details";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { DCashRecoveryInit } from "../../screens/more/dcash-recovery-init";

/*
 ****************************************
 *******Redesigned Screens & Flows*******
 ****************************************
 */

import PaymentAutoPayDetails from "../../screens/new/payment-autopay-details";
import PaymentAutoPayPaymentSummary from "../../screens/new/payment-autopay-payment-summary";
import PaymentAutoPayReviewPayment from "../../screens/new/payment-autopay-review-payment";

import PaymentFlowPaymentDetails from "../../screens/new/payment-flow-payment-details";
import PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment from "../../screens/new/payment-flow-payment-method-certified-ach-yodlee-widget-review-payment";
import PaymentFlowPaymentMethodCertifiedACHYodleeWidget from "../../screens/new/payment-flow-payment-method-certified-ach-yodlee-widget";
import PaymentFlowPaymentMethodCertifiedACH from "../../screens/new/payment-flow-payment-method-certified-ach";
import PaymentFlowPaymentMethodMobileCheckPaymentInstructions from "../../screens/new/payment-flow-payment-method-mobile-check-payment-instructions";
import PaymentFlowPaymentMethodMobileCheckPaymentProcessing from "../../screens/new/payment-flow-payment-method-mobile-check-payment-processing";
import PaymentFlowPaymentMethodMoneyGram from "../../screens/new/payment-flow-payment-method-moneygram";
import PaymentFlowPaymentMethod from "../../screens/new/payment-flow-payment-method";
import PaymentFlowPaymentSummary from "../../screens/new/payment-flow-payment-summary";
import PaymentFlowPaymentTrasmitted from "../../screens/new/payment-flow-payment-trasmitted";
import WalletPaymentMethods from "../../screens/new/wallet-payment-methods";
import { PaymentFlowPaymentMethodMobileCheckPaymentCamera } from "../../screens/new/payment-flow-payment-method-mobile-check-payment-camera";
import PaymentFlowPaymentMethodMobileCheckPaymentSummary from "../../screens/new/payment-flow-payment-method-mobile-check-payment-summary";
import ChangePassword from "../../screens/new/change-password";
import { Rewards } from "../../screens/new/rewards";
import { RewardsHistory } from "../../screens/new/rewards-history";

const { PAYMENT_STARTED, PAYMENT_IN_REVIEW, PAYMENT_CREATED } = PaymentActionTypes;

const Stack = createStackNavigator();
const getBalanceDue = (route: any) => {
  const balanceDue = route?.params?.balanceDue;
  return balanceDue;
};

const getPropName = (route: any) => {
  const propName = route?.params?.propName;
  return propName;
};

export const ActionsStack = () => {
  const appMode = useSelector((state: RootState) => state.reservation.appMode);
  const { firstReservationId } = useReservationInformation();

  // console.log(`appMode: ${appMode}`);
  if (appMode == AppMode.DCASH_RECOVERY) {
    return (
      <Stack.Navigator screenOptions={{ headerMode: "screen", showFullMenu: false }}>
        <Stack.Screen
          name="DCashRecoveryEnterPaymentDetails"
          component={DCashRecoveryEnterPaymentDetails}
          options={({ route }) => ({
            header: (props) => <Header {...props} />,
            title: translate("dcashRecoveryStack.title") as string,
            paymentStatus: PAYMENT_STARTED,
            balanceDue: getBalanceDue(route),
          })}
        />
        <Stack.Screen
          name="AddBankAccount"
          component={AddBankAccount}
          options={({ route }) => ({
            header: (props) => <Header {...props} />,
            title: translate("homeStack.addBankAccount") as string,
            showBalance: false,
            useLight: true,
            omitBackButton: false,
            omitRightContent: true,
          })}
        />
        <Stack.Screen
          name="AddCreditCard"
          component={AddCreditCard}
          options={({ route }) => ({
            header: (props) => <Header {...props} />,
            title: translate("homeStack.addCreditCard") as string,
            showBalance: false,
            useLight: true,
            omitBackButton: false,
            omitRightContent: true,
          })}
        />
        <Stack.Screen
          name="ReviewPayment"
          component={ReviewPayment}
          options={({ route }) => ({
            header: (props) => <Header {...props} />,
            title: translate("dcashRecoveryStack.title") as string,
            paymentStatus: PAYMENT_IN_REVIEW,
            balanceDue: getBalanceDue(route),
          })}
        />
        <Stack.Screen
          name="PaymentSummary"
          component={PaymentSummary}
          options={({ route }) => ({
            header: (props) => <Header {...props} />,
            title: translate("homeStack.confirmTitle") as string,
            useLight: true,
            omitBackButton: true,
            omitRightContent: true,
          })}
        />
        <Stack.Screen
          name="DCashRecoveryInit"
          component={DCashRecoveryInit}
          options={({ route }) => ({
            header: (props) => <Header {...props} />,
            title: translate("dcashRecoveryInit.title"),
            omitBackButton: true,
            omitRightContent: true,
            useLight: false,
          })}
        />
      </Stack.Navigator>
    );
  }

  return (
    <Stack.Navigator screenOptions={{ headerMode: "screen", showFullMenu: true }}>
      <Stack.Screen
        name="PaymentSummary"
        component={PaymentSummary}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("homeStack.confirmTitle") as string,
          useLight: false,
          omitBackButton: true,
          omitRightContent: true,
        })}
      />
      <Stack.Screen
        name="AddCreditCard"
        component={AddCreditCard}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("homeStack.addCreditCard") as string,
          showBalance: false,
          useLight: true,
          omitBackButton: false,
          omitRightContent: true,
        })}
      />
      <Stack.Screen
        name="VerifyBillingAddress"
        component={VerifyBillingAddress}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: "Verify Billing Address",
          showBalance: false,
          useLight: true,
          omitBackButton: false,
          omitRightContent: true,
        })}
      />
      <Stack.Screen
        name="AddBankAccount"
        component={AddBankAccount}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("homeStack.addBankAccount") as string,
          showBalance: false,
          useLight: true,
          omitBackButton: false,
          omitRightContent: true,
        })}
      />

      {/*
       ****************************************
       *******Redesigned Screens & Flows*******
       ****************************************
       */}
      <Stack.Screen
        name="PaymentAutoPayDetails"
        component={PaymentAutoPayDetails}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("autopay.workflowTitle") as string,
        })}
      />
      <Stack.Screen
        name="PaymentAutoPayPaymentSummary"
        component={PaymentAutoPayPaymentSummary}
        options={({ route }) => ({
          header: (props) => <View />,
          title: translate("homeStack.confirmTitle") as string,
          useLight: true,
          omitBackButton: true,
          omitRightContent: true,
        })}
      />
      <Stack.Screen
        name="PaymentAutoPayReviewPayment"
        component={PaymentAutoPayReviewPayment}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("autopay.workflowTitle") as string,
        })}
      />

      <Stack.Screen
        name="PaymentFlowPaymentDetails"
        component={PaymentFlowPaymentDetails}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("paymentFlow.workflowTitle") as string,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment"
        component={PaymentFlowPaymentMethodCertifiedACHYodleeWidgetReviewPayment}
        options={({ route }: any) => ({
          header: (props) => <Header {...props} />,
          title: translate("paymentMethods.certifiedBankAccount.title") as String,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodCertifiedACHYodleeWidget"
        component={PaymentFlowPaymentMethodCertifiedACHYodleeWidget}
        options={({ route }) => ({ title: "", omitRightContent: true })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodCertifiedACH"
        component={PaymentFlowPaymentMethodCertifiedACH}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("paymentMethods.certifiedBankAccount.title") as String,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodMobileCheckPaymentInstructions"
        component={PaymentFlowPaymentMethodMobileCheckPaymentInstructions}
        options={({ route }: any) => ({
          header: (props) => <Header {...props} />,
          title: route.params.headerTitle ? route.params.headerTitle : "",
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodMobileCheckPaymentProcessing"
        component={PaymentFlowPaymentMethodMobileCheckPaymentProcessing}
        options={({ route }: any) => ({
          header: (props) => <Header {...props} />,
          title: route.params.headerTitle,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodMobileCheckPaymentSummary"
        component={PaymentFlowPaymentMethodMobileCheckPaymentSummary}
        options={({ route }: any) => ({
          header: (props) => <Header {...props} />,
          title: route.params.headerTitle,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodMobileCheckPaymentCamera"
        component={PaymentFlowPaymentMethodMobileCheckPaymentCamera}
        options={({ route }) => ({
          header: (props) => null,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethodMoneyGram"
        component={PaymentFlowPaymentMethodMoneyGram}
        options={({ route }: any) => ({
          header: (props) => <Header {...props} />,
          title: translate("paymentFlow.paymentMethodMoneygram.workflowTitle") as string,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentMethod"
        component={PaymentFlowPaymentMethod}
        options={({ route }) => ({
          header: (props) => <Header {...props} />,
          title: translate("paymentFlow.paymentMethod.workflowTitle") as string,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentSummary"
        component={PaymentFlowPaymentSummary}
        options={({ route }: any) => ({
          header: (props) => <Header {...props} />,
          title: translate("paymentFlow.summary.workflowTitle") as string,
        })}
      />
      <Stack.Screen
        name="PaymentFlowPaymentTrasmitted"
        component={PaymentFlowPaymentTrasmitted}
        options={({ route }: any) => ({
          headerShown: false,
        })}
      />

      <Stack.Screen
        name="WalletPaymentMethods"
        component={WalletPaymentMethods}
        options={({ route, navigation }) => ({
          header: (props) => <Header {...props} />,
          title: translate("walletPaymentMethod.workflowTitle") as string,
        })}
      />
      <Stack.Screen
        name="ChangePassword"
        component={ChangePassword}
        options={({ route, navigation }) => ({
          header: (props) => <Header {...props} />,
          title: translate("moreStack.changePassword") as string,
        })}
      />
      <Stack.Screen
        name="Rewards"
        component={Rewards}
        options={({ route, navigation }) => ({
          header: (props) => <Header {...props} />,
          omitTitleHeader: true,
        })}
      />
      <Stack.Screen
        name="RewardsHistory"
        component={RewardsHistory}
        options={({ route, navigation }) => ({
          header: (props) => <Header {...props} />,
          omitTitleHeader: true,
        })}
      />
    </Stack.Navigator>
  );
};

export interface ActionParamsList extends Record<string, any> {}

export type ActionScreenProps = { route: { params: ActionParamsList } };
